import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

const LeadSearchInput = ({ value, onChange, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const dropdownRef = useRef(null);
  const searchTimeout = useRef(null);

  // Load selected lead details on mount if value exists
  useEffect(() => {
    const fetchLeadDetails = async () => {
      if (value) {
        try {
          const token = localStorage.getItem('token');
          const config = {
            headers: {
              'x-auth-token': token
            }
          };
          
          const res = await axios.get(`/api/leads/${value}`, config);
          setSelectedLead({ 
            id: res.data.id, 
            name: res.data.name, 
            email: res.data.email 
          });
          setSearchTerm(res.data.name || res.data.email || '');
        } catch (err) {
          console.error('Error fetching lead details:', err);
          setSelectedLead(null);
          setSearchTerm('');
        }
      } else {
        setSelectedLead(null);
        setSearchTerm('');
      }
    };

    fetchLeadDetails();
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Debounced search function
  const searchLeads = async (query) => {
    if (!query || query.length < 2) {
      setLeads([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    try {
      // Clear any existing timeout
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }

      // Set a new timeout (300ms debounce)
      searchTimeout.current = setTimeout(async () => {
        const token = localStorage.getItem('token');
        const config = {
          headers: {
            'x-auth-token': token
          }
        };

        const res = await axios.get(`/api/leads?search=${encodeURIComponent(query)}&limit=10`, config);
        setLeads(res.data.leads || []);
        setShowDropdown(true);
        setLoading(false);
      }, 300);
    } catch (err) {
      console.error('Error searching leads:', err);
      setLeads([]);
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchTerm(query);
    searchLeads(query);
  };

  const handleSelectLead = (lead) => {
    setSelectedLead(lead);
    setSearchTerm(lead.name || lead.email || '');
    setShowDropdown(false);
    onChange(lead.id);
  };

  const handleClearSelection = () => {
    setSelectedLead(null);
    setSearchTerm('');
    onChange('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex items-center">
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={() => searchTerm.length >= 2 && setShowDropdown(true)}
          placeholder={placeholder || "Search leads by name or email"}
          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
        />
        {selectedLead && (
          <button
            type="button"
            onClick={handleClearSelection}
            className="absolute right-2 text-gray-400 hover:text-gray-600"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-auto">
          {loading ? (
            <div className="p-2 text-center text-gray-500">
              <svg className="animate-spin h-5 w-5 mx-auto text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : leads.length === 0 ? (
            <div className="p-2 text-center text-gray-500">No leads found</div>
          ) : (
            <ul>
              {leads.map(lead => (
                <li
                  key={lead.id}
                  onClick={() => handleSelectLead(lead)}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  <div className="font-medium">{lead.name}</div>
                  <div className="text-sm text-gray-500">{lead.email}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

LeadSearchInput.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string
};

export default LeadSearchInput;